<script setup>
import { ref, watchEffect } from "vue";
import RequiredInformations from "@/components/shared/RequiredInformations.vue";
import { useStore } from "vuex";
import {
  citizenEvent,
  getOffices,
  getResource,
  getTopics,
  searchResources,
  updateQueryParams,
} from "@/api/httpApi.js";
import {
  validateForm, showFormToast
} from "@/utility.js";
import CardAvvertenze from "./CardAvvertenze.vue";
import CardPrivacy from "./CardPrivacy.vue";
import RadioResource from "./RadioResource.vue";
import CardResource from "../shared/CardResource.vue";
import { onMounted } from 'vue';
import * as Yup from "yup";

/*
* Filtri e ricerca libera
* Per mostrare o nascondere i filtri di ricerca e la ricerca libera passare alla funzione ref il valore true per mostrare e false per nascondere
*/
const enableOfficeShowHide = ref(false); // Mostra o Nasconde il pulsante "nascondi" filtro per ufficio
const enableTopicShowHide = ref(false); // Mostra o Nasconde il pulsante "nascondi" filtro per argomento
const showSearchSection = ref(false); // Mostra o Nasconde il campo di ricerca libera

const store = useStore();
const emit = defineEmits(["next"]);
const informations = ref(["Ufficio", "Argomento", "Ricerca"]);
const offices = ref(undefined);
const topics = ref(undefined);
const resources = ref(undefined);
const resourceDetails = ref(undefined);
const searched = ref(store.state.searched || false);
const officesCollapsed = ref(false);
const topicsCollapsed = ref(false);
const searchCollapsed = ref(false);
const selectedOffice = ref(store.state.officeId || "");
const selectedTopic = ref(store.state.topicId || "");
const searchText = ref(store.state.searchText || "");
const selectedResource = ref(store.state.resourceId || "");
const reservationId = ref(store.state.reservationId || "");
const searchDirectFromUrl = ref(store.state.searchDirectFromUrl || false);
const isEditing = ref(store.state.isEditing || false);
const refAvvertenze = ref(null);
const checkedPrivacy = ref(false);
const pageSize = ref(5);
const pageNumber = ref(0);
//const totalElements = ref(0);
const totalPages = ref(1);
const officeSelectPlaceholder = ref(true);
const topicSelectPlaceholder = ref(true);
const step1FormA = ref();
const step1FormB = ref();

onMounted(() => {
  getResourceDetail();
  createFormA();
  createFormB();
});

watchEffect(async () => {
  // Load the offices data
  if (!offices.value) {
    try {
      offices.value = await getOffices();
    } catch (err) {
      console.error(err);
      offices.value = undefined;
    }
  }
});

watchEffect(async () => {
  // Load the topics data
  if (!topics.value) {
    try {
      topics.value = await getTopics();
    } catch (err) {
      console.error(err);
      topics.value = undefined;
    }
  }
});

async function updateOfficeSelect(value) {
  try {
    offices.value = (value === '') ? await getOffices() : await getOffices(value);
    if(value !== '') {
      topicSelectPlaceholder.value = false;
    }
  } catch (err) {
    console.error(err);
    offices.value = undefined;
  }
}

async function updateTopicSelect(value) {
  try {
    topics.value = (value === '') ? await getTopics() : await getTopics(value);
    if(value !== '') {
      officeSelectPlaceholder.value = false;
    }
  } catch (err) {
    console.error(err);
    topics.value = undefined;
  }
}

function createFormA() {
  // // Schema di validazione - libreria Yup
  if(!showSearchSection.value) {
    step1FormA.value = Yup.object().shape({
      fieldSelectOffice: Yup.string().when("fieldSelectTopic", {
        is: (value) => value == '',
        then: (schema) => schema.required("Campo obbligatorio"),
        otherwise: (schema) => schema.nullable()
      }),
      fieldSelectTopic: Yup.string().when("fieldSelectOffice", {
        is: (value) => value == '',
        then: (schema) =>  schema.required("Campo obbligatorio"),
        otherwise: (schema) => schema.nullable()
      })
    }, [ [ 'fieldSelectOffice', 'fieldSelectTopic' ] ]);
  } else {
    step1FormA.value = Yup.object().shape({
      fieldSelectOffice: Yup.string().when(["fieldSelectTopic", "fieldSearchText"], {
        is: (topicValue, searchValue) => topicValue === '' && searchValue === '',
        then: (schema) => schema.required("Campo obbligatorio"),
        otherwise: (schema) => schema.nullable()
      }),
      fieldSelectTopic: Yup.string().when(["fieldSelectOffice", "fieldSearchText"], {
        is: (officeValue, searchText) => officeValue === '' && searchText === '',
        then: (schema) => schema.required("Campo obbligatorio"),
        otherwise: (schema) => schema.nullable()
      }),
      fieldSearchText: Yup.string().when(["fieldSelectOffice", "fieldSelectTopic"], {
        is: (officeValue, topicValue) => officeValue === '' && topicValue === '',
        then: (schema) => schema.required("Campo obbligatorio"),
        otherwise: (schema) => schema.nullable()
      })
    }, [
      ["fieldSelectOffice", "fieldSelectTopic"],
      ["fieldSelectOffice", "fieldSearchText"],
      ["fieldSelectTopic", "fieldSearchText"],
    ]);
  }
}

function createFormB() {
  // Schema di validazione - libreria Yup
  step1FormB.value = Yup.object().shape({
    resource_radio_group: Yup.string().required('Campo obbligatorio'),
    checkboxPrivacy: Yup.boolean().when("resource_radio_group", {
      is: (value) => value?.length > 0,
      then: (schema) =>  schema.oneOf([true], 'Campo obbligatorio').required("Campo obbligatorio"),
      otherwise: (schema) => schema.nullable()
    })
  });
}

function resetSearch() {
  searched.value = false;
  resources.value = undefined;
  updateQueryParams([]);
  informations.value = ["Ufficio", "Argomento", "Ricerca"];
  selectedResource.value = undefined;

  const elementToTop = document.getElementById("top-stepper");

  if (elementToTop) {
    setTimeout(() => {
      window.scrollTo({
        top: elementToTop.offsetTop - elementToTop.offsetHeight,
        left: 0,
        behavior: "smooth",
      });
    }, 500);
  }
}

async function getResourceDetail() {
  if(selectedResource.value !== '') {

    if (searchDirectFromUrl.value && (selectedResource.value !== '') && !isEditing.value) {
      citizenEvent(
        {
          message: "Utente inizia prenotazione con risorsa pre-selezionata",
          stepId: 1,
          stepType: "inizio",
          resourceId: selectedResource.value
        }
      );
    } else if(store.state.isEditing) {
      citizenEvent(
        {
          message: "Utente modifica prenotazione " + reservationId.value,
          stepId: 1,
          stepType: "intermedio",
          resourceId: selectedResource.value
        }
      );
    }

    const resourceDetailsData = await getResource(selectedResource.value);
    resourceDetails.value = resourceDetailsData;

    if (resourceDetailsData) {
      setTimeout(() => {
        window.scroll({
          top: refAvvertenze.value?.offsetTop || 600,
          left: 0,
          behavior: "smooth",
        });
      }, 200);
    }
  } else {
    citizenEvent(
      {
        message: "Utente inizia prenotazione",
        stepId: 1,
        stepType: "inizio",
        resourceId: selectedResource.value
      }
    );
  }
}

watchEffect(async () => {
  // Update the store
  store.commit("setSearched", searched.value);
  store.commit("setResourceId", selectedResource.value);
  store.commit("setTopicId", selectedTopic.value);
  store.commit("setOfficeId", selectedOffice.value);
  store.commit("setSearchText", searchText.value);
});

watchEffect(async () => {
  // Update the query params
  if (
    searched.value &&
    (selectedTopic.value ||
      selectedOffice.value ||
      searchText.value ||
      searchText.value)
  ) {
    updateQueryParams([
      ...(selectedTopic.value
        ? [{ key: "topicId", value: selectedTopic.value }]
        : []),
      ...(selectedOffice.value
        ? [{ key: "officeId", value: selectedOffice.value }]
        : []),
      ...(searchText.value
        ? [{ key: "searchText", value: searchText.value }]
        : []),
      ...(selectedResource.value
        ? [{ key: "resourceId", value: selectedResource.value }]
        : []),
    ]);
  }
});


watchEffect(async () => {
  // Trigger the search
  if (
    (selectedTopic.value || selectedOffice.value || searchText.value) &&
    offices.value &&
    topics.value &&
    searched.value &&
    !resources.value
  ) {
    try {
      const searchResourcesData = await searchResources({
        name: searchText.value,
        officeId: selectedOffice.value,
        topicId: selectedTopic.value,
        pageSize: pageSize.value,
        pageNumber: pageNumber.value,
      });
      resources.value = searchResourcesData.content;
      totalPages.value = searchResourcesData.totalPages;
      informations.value = ["Ufficio", "Argomento", "Ricerca", "Avvertenze"];
    } catch (error) {
      console.error(error);
      resources.value = undefined;
    }
  }
});

watchEffect(async () => {
  if (!searched.value && selectedOffice.value && officesCollapsed.value) {
    selectedOffice.value = "";
  }
});

watchEffect(async () => {
  if (!searched.value && selectedTopic.value && topicsCollapsed.value) {
    selectedTopic.value = "";
  }
});


async function nextStep(step, autoSubmit) {
  setTimeout(() => {
    if (step === 'step1A') {
      validateForm(step1FormA.value, 'step1FormA', {
        fieldSelectOffice: selectedOffice.value,
        fieldSelectTopic: selectedTopic.value,
        fieldSearchText: searchText.value
      }, () => {
        if (autoSubmit) {
          // se tutto è valido procedo allo step successivo
          formValid();
        }
      }, (errors) => {
        // in presenza di errori di validazione mostro il toast di errore
        showFormToast('step1FormA', errors, 'Per poter procedere compilare uno dei campi:');
      }, () => {
        // dopo la prima validazione resto in ascolto di ogni cambiamento di input per rieseguire una validazione in tempo reale
        nextStep('step1A', false);
      });

    } else {
      validateForm(step1FormB.value, 'step1FormB', {
        resource_radio_group: selectedResource.value,
        checkboxPrivacy: JSON.parse(checkedPrivacy.value)
      }, () => {
        if (autoSubmit) {
          // se tutto è valido procedo allo step successivo

          citizenEvent(
            {
              message: "Utente seleziona risorsa",
              stepId: 1,
              stepType: "intermedio",
              resourceId: selectedResource.value
            }
          );

          formValid();
        }
      }, (errors) => {
        // in presenza di errori di validazione mostro il toast di errore
        showFormToast('step1FormB', errors);
      }, () => {
        // dopo la prima validazione resto in ascolto di ogni cambiamento di input per rieseguire una validazione in tempo reale
        nextStep('step1B', false);
      });
    }
  }, 300);
}

function formValid() {
  if (!searched.value) {
    searched.value = true;
    const elementToTop = document.getElementById("top-stepper");

    if (elementToTop) {
      setTimeout(() => {
        window.scrollTo({
          top: elementToTop.offsetTop - elementToTop.offsetHeight,
          left: 0,
          behavior: "smooth",
        });
      }, 500);
    }

    document.getElementById("top-stepper-progress-bar").focus();
  } else {
    try {
      // Ensure data is clean
      store.commit("setNumberOfReservations", 1);
      store.commit("setYear", undefined);
      store.commit("setMonth", undefined);
      store.commit("setDay", undefined);
      store.commit("setHour", undefined);
      store.commit("setSlots", []);
      store.commit("setReservationId", undefined);
      store.commit("setServiceId", undefined);
      store.commit("setDetail", "");
      store.commit("setAttributes", {});
      store.commit("setUser", {});
      store.commit("setIsRecapping", false);
      store.commit("setIdentifyValues", []);
      updateQueryParams([]);
      emit("next");
    } catch (error) {
      console.error(error);
    }
  }
}

function setPage(page) {
  pageNumber.value = page;
  resources.value = undefined;

  const elementToTop = document.getElementById("top-stepper");

  if (elementToTop) {
    setTimeout(() => {
      window.scrollTo({
        top: elementToTop.offsetTop - elementToTop.offsetHeight,
        left: 0,
        behavior: "smooth",
      });
    }, 500);
  }
}
</script>
<style scoped>
.error {
  color: #d9364f;
}
</style>
<template>

<div class="container-fluid row mx-auto p-2">
  <div
    class="col-3 border-end pe-0 d-none d-md-block"
    id="top-stepper-progress-bar">
    <RequiredInformations :informations="informations" :progress="20" />
  </div>

  <div class="col-12 px-0 ps-md-5 pe-md-2 pt-4 mb-5 col-md-9">

<!-- Testo descrittivo dei filtri -->
<!--    <div class="row ml-4">-->
<!--      <div class="col-12">-->
<!--        <p role="heading" tabindex="0" aria-label="Ufficio aria Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Argomento aria Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.">Ufficio aria Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Argomento aria Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>-->
<!--      </div>-->
<!--    </div>-->

    <form id="step1FormA" @submit.prevent autocomplete="off">
      <div
        class="bg-light p-4 shadow-sm"
        v-show="!searched"
        :id="encodeURIComponent('Ufficio')">

        <h2 class="h3">Ufficio</h2>

        <p class="card-text">Scegli l'ufficio per cui vuoi richiedere l'appuntamento</p>

        <div v-bind:class="{ 'collapse': enableOfficeShowHide, 'show': enableOfficeShowHide }" :id="enableOfficeShowHide ? `collapseOffice` : null">
          <div class="select-wrapper">
            <select v-model="selectedOffice" aria-label="Filtra per ufficio"
                    @change="updateTopicSelect($event.target.value)"
                    id="fieldSelectOffice"
                    name="fieldSelectOffice"
                    data-yup-label="Ufficio"
                    >
              <option selected="" value="">{{ officeSelectPlaceholder ? "Seleziona ufficio" : "Nessun ufficio" }}</option>
              <option
                v-for="office in offices || []"
                :key="office.officeId"
                :value="office.officeId">
                {{ office.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="mt-4" v-show="enableOfficeShowHide">
          <a
            style="text-decoration: none"
            data-bs-toggle="collapse"
            href="#collapseOffice"
            role="button"
            aria-expanded="false"
            aria-controls="collapseOffice"
            @click="
            () => {
              officesCollapsed = !officesCollapsed;
            }">
            {{ officesCollapsed ? "Filtra per ufficio" : "Nascondi" }}
            <svg class="icon icon-primary">
              <use
                :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-${
                officesCollapsed ? 'expand' : 'collapse'
              }`"></use>
            </svg>
          </a>
        </div>
      </div>

      <div
        class="bg-light p-4 shadow-sm mt-5"
        v-show="!searched"
        :id="encodeURIComponent('Argomento')">

        <h2 class="h3">Argomento</h2>

        <p class="card-text">Scegli l'argomento per cui vuoi richiedere un appuntamento</p>

        <div v-bind:class="{ 'collapse': enableTopicShowHide, 'show': enableTopicShowHide }" :id="enableTopicShowHide ? `collapseTopic` : null">
          <div class="select-wrapper">
            <select v-model="selectedTopic" aria-label="Filtra per argomento"
                    @change="updateOfficeSelect($event.target.value)"
                    id="fieldSelectTopic"
                    name="fieldSelectTopic"
                    data-yup-label="Argomento"
                    >
              <option selected="" value="">{{ topicSelectPlaceholder ? "Seleziona argomento" : "Nessun argomento" }}</option>
              <option
                v-for="topic in topics || []"
                :key="topic.topicId"
                :value="topic.topicId">
                {{ topic.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="mt-4" v-show="enableTopicShowHide">
          <a
            style="text-decoration: none"
            data-bs-toggle="collapse"
            href="#collapseTopic"
            role="button"
            aria-expanded="false"
            aria-controls="collapseTopic"
            @click="
            () => {
              topicsCollapsed = !topicsCollapsed;
            }
          "
          >
            {{ topicsCollapsed ? "Filtra per argomento" : "Nascondi" }}
            <svg class="icon icon-primary">
              <use
                :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-${
                topicsCollapsed ? 'expand' : 'collapse'
              }`"
              ></use>
            </svg>
          </a>
        </div>

      </div>

      <div
        class="bg-light p-4 shadow-sm mt-5"
        v-show="!searched && showSearchSection"
        :id="encodeURIComponent('Ricerca')">

        <h2 class="h3">Ricerca</h2>

        <p class="card-text">Scrivi di cosa hai bisogno</p>

        <div class="collapse show" id="collapseSearch">
          <div class="form-group mb-4">
            <input
              id="fieldSearchText"
              name="fieldSearchText"
              data-yup-label="Ricerca"
              type="text"
              class="form-control bg-white"
              placeholder="Cerca..."
              v-model="searchText" />
          </div>
        </div>

        <div class="mt-4">
          <a
            style="text-decoration: none"
            data-bs-toggle="collapse"
            href="#collapseSearch"
            role="button"
            aria-expanded="false"
            aria-controls="collapseSearch"
            @click="
            () => {
              searchCollapsed = !searchCollapsed;
            }">

            {{ searchCollapsed ? "Filtra per parole chiave" : "Nascondi" }}

            <svg class="icon icon-primary">
              <use
                :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-${
                searchCollapsed ? 'expand' : 'collapse'
              }`"
              ></use>
            </svg>
          </a>
        </div>

      </div>

      <div class="d-flex justify-content-between mt-4" v-if="!searched">
        <button
          v-if="searched"
          type="button"
          class="btn btn-outline-primary"
          @click="resetSearch">
          Indietro
        </button>
        <div v-else></div>

        <button
          type="submit"
          class="btn btn-outline-primary"
          @click="nextStep('step1A', true)">
          Avanti
        </button>
      </div>
    </form>

    <form id="step1FormB" @submit.prevent autocomplete="off">

      <div
        class="bg-light p-4 shadow-sm"
        v-show="searched && resources">

        <h2 class="card-title h6 mb-1" style="text-decoration: underline">
          {{
            [
              ...(selectedOffice
                ? [
                  `Ufficio: ${
                    offices?.find(
                      (office) => office.officeId === selectedOffice
                    )?.name || ""
                  }`,
                ]
                : []),
              ...(selectedTopic
                ? [
                  `Argomento: ${
                    topics?.find((office) => office.topicId === selectedTopic)
                      ?.name || ""
                  }`,
                ]
                : []),
              ...(searchText ? [`Ricerca: ${searchText}`] : []),
            ].join(" / ")
          }}
        </h2>
        <p class="card-text mb-0">
          {{
            resources?.length > 0
              ? `Scegli il servizio per cui richiedere appuntamento. In base ai filtri impostati nel passo precedente ${(resources?.length > 1) ? `risultano` : `risulta`} ${(resources?.length > 1) ? `disponibili` : `disponibile`} ${resources?.length} ${(resources?.length > 1) ? `servizi` : `servizio`}.`
              : "Nessuna agenda trovata con i filtri ricerca impostati"
          }}
        </p>

          <div class="d-flex flex-column" id="step1FormB_resource_radio_group">
            <RadioResource
              v-for="(resource, index) in resources"
              :yupErrorIdContainer="`step1FormB_resource_radio_group`"
              :id="`step1FormB_resource_radio_group_` + (index + 1)"
              :name="`resource_radio_group`"
              :key="resource.resourceId"
              :resource="resource"
              :isSelected="selectedResource === resource.resourceId"
              @change="
                (value) => {
                  selectedResource = value;
                  getResourceDetail();
                }
              "/>
          </div>

        <nav
          class="pagination-wrapper mt-4"
          aria-label="Esempio di navigazione della pagina"
          style="justify-content: center"
        >
          <ul class="pagination" v-if="totalPages > 1">
            <li class="page-item" v-if="pageNumber > 0">
              <a
                class="page-link"
                href="#"
                @click="
                  (e) => {
                    e.preventDefault();
                    setPage(pageNumber - 1);
                  }
                "
              >
                <svg class="icon icon-primary">
                  <use
                    href="/${store.state.modulePath}/assets/svg/sprites.svg#it-chevron-left"
                  ></use>
                </svg>
                <span class="visually-hidden">Pagina precedente</span>
              </a>
            </li>
            <li
              class="page-item"
              v-for="page in Array.from(
                { length: totalPages },
                (_, i) => i + 1
              )"
              :key="page"
            >
              <a
                class="page-link"
                href="#"
                v-if="page === pageNumber + 1"
                aria-current="page"
                @click="
                  (e) => {
                    e.preventDefault();
                    setPage(page - 1);
                  }
                "
              >{{ page }}</a
              >
              <a
                class="page-link"
                href="#"
                v-else
                @click="
                  (e) => {
                    e.preventDefault();
                    setPage(page - 1);
                  }
                "
              >{{ page }}</a
              >
            </li>
            <li class="page-item" v-if="pageNumber < totalPages">
              <a
                class="page-link"
                href="#"
                @click="
                  (e) => {
                    e.preventDefault();
                    setPage(pageNumber + 1);
                  }
                "
              >
                <span class="visually-hidden">Pagina successiva</span>
                <svg class="icon icon-primary">
                  <use
                    href="/${store.state.modulePath}/assets/svg/sprites.svg#it-chevron-right"
                  ></use>
                </svg>
              </a>
            </li>
          </ul>
        </nav>
      </div>

      <CardResource
        v-if="searched && resourceDetails && resources === undefined"
        :resource="resourceDetails"/>

      <div
        ref="refAvvertenze"
        v-if="selectedResource && resourceDetails"
        :id="encodeURIComponent('Avvertenze')">
      </div>

      <div ref="refAvvertenze"
           v-if="selectedResource && resourceDetails">
      </div>

      <CardAvvertenze
        v-if="selectedResource && resourceDetails"
        :resource="resourceDetails"/>

      <CardPrivacy
        :id="`checkboxPrivacy`"
        :name="`checkboxPrivacy`"
        v-if="selectedResource && resourceDetails"
        :checkedPrivacy="checkedPrivacy"
        :privacyText="resourceDetails?.privacyNote || ''"
        @change="
          (value) => {
            checkedPrivacy = value;
          }"/>

      <div class="d-flex justify-content-between mt-4" v-if="searched || selectedResource">
        <button
          v-if="searched"
          type="button"
          class="btn btn-outline-primary"
          @click="resetSearch">
          Indietro
        </button>
        <div v-else></div>
        <button
          v-if="selectedResource"
          type="submit"
          class="btn btn-outline-primary"
          @click="nextStep('step1B', true)">
          Avanti
        </button>
      </div>
    </form>

  </div>
</div>
</template>
